/**
 * Audio Controller — Singleton WebAudio synthesis engine.
 *
 * Owns a single, lazily-created `AudioContext` for the page lifetime and exposes
 * a small *semantic* interface (playMove / playCapture / playCheck / playGameOver)
 * so callers (the socket controller) fire-and-forget without knowing anything
 * about oscillators or gain envelopes. The synthesis engine is intentionally
 * abstracted: swap it for HTML5 `<audio>` samples later by re-implementing these
 * methods — no consumer change required.
 *
 * Autoplay policy (Browser Autoplay Policy / iOS):
 * - Browsers block `AudioContext` playback until a user gesture. The controller
 *   attaches a ONE-TIME global `pointerdown` + `keydown` listener (capture phase)
 *   that resumes the context and then unbinds itself. `unlockAudio()` is also
 *   called explicitly from the sound toggle button so toggling also unlocks.
 * - Every `play*` method is autoplay-safe: if the context is still suspended, or
 *   sound is disabled, or the environment has no `AudioContext`, it no-ops with no
 *   console noise. Non-blocking by contract — all methods return `void`.
 *
 * Memory: one shared `AudioContext`; per-tone oscillators disconnect themselves
 * on `ended` so the graph never accumulates nodes.
 */

/** Outcome semantic for the game-over chime. */
export type GameOverOutcome = 'win' | 'lose' | 'draw';

/** Frequency (Hz), seconds. */
type ToneSpec = {
  freq: number;
  type: OscillatorType;
  duration: number;
  /** Peak gain (0..1). */
  gain: number;
  /** Optional second stage (a second oscillator started alongside, e.g. noise/clack). */
  second?: {
    freq: number;
    type: OscillatorType;
    duration: number;
    gain: number;
    detune?: number;
  };
};

/** Resilient AudioContext constructor (vendor + SSR safe). */
function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  );
}

class AudioController {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private enabled = true;
  private unlockBound = false;
  private unlocked = false;

  /** Lazily create the AudioContext + master gain. SSR/unsupported → null. */
  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = getAudioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      // Modest master ceiling so layered tones never clip.
      this.master.gain.value = 0.4;
      this.master.connect(this.ctx.destination);
      this.attachUnlockListener();
    } catch {
      this.ctx = null;
      this.master = null;
    }
    return this.ctx;
  }

  /** One-time global gesture listener that resumes the (autoplay-locked) context. */
  private attachUnlockListener(): void {
    if (this.unlockBound || typeof window === 'undefined') return;
    this.unlockBound = true;
    const unlock = (): void => {
      // Create-on-demand so a page that never needed audio pays no cost.
      const ctx = this.ensureContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      this.unlocked = true;
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    // Capture phase so we beat component handlers; these never block default.
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }

  /** Public: explicitly resume the context (called from the sound toggle). */
  unlockAudio(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    this.unlocked = true;
  }

  /** Public: is the context currently resumable/resumed? Diagnostic only. */
  isAudioUnlocked(): boolean {
    return this.unlocked;
  }

  /** Public: enable/disable playback. Driven by useSettingsStore. */
  setSoundEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** Public: is playback currently enabled? */
  isSoundEnabled(): boolean {
    return this.enabled;
  }

  // ---------------------------------------------------------------------
  // Semantic triggers (fire-and-forget). All autoplay- and guard-safe.
  // ---------------------------------------------------------------------

  /** `move:applied` — a solid low "thud" (sine, ~120Hz, ~120ms). */
  playMove(): void {
    this.play({ freq: 120, type: 'sine', duration: 0.12, gain: 0.55 });
  }

  /** `piece:captured` — a sharper "clack" (triangle ~220Hz + a noisy octave-up sib). */
  playCapture(): void {
    this.play({
      freq: 220,
      type: 'triangle',
      duration: 0.1,
      gain: 0.6,
      second: { freq: 440, type: 'square', duration: 0.06, gain: 0.22, detune: -8 },
    });
  }

  /** `check:warning` — a low tension drone (sawtooth ~80Hz, slow swell, ~0.5s). */
  playCheck(): void {
    this.play({ freq: 80, type: 'sawtooth', duration: 0.5, gain: 0.4 });
  }

  /** `game:over` — distinct chime by outcome (truth is revealed at game:over). */
  playGameOver(outcome: GameOverOutcome): void {
    // Major-third ascending pair = lift/victory; minor pair descending = loss;
    // a single neutral bell for a draw. Sequencing is done by start-time offsets.
    const nowOffset = (s: number): number =>
      (this.ctx?.currentTime ?? 0) + s;
    if (outcome === 'win') {
      this.scheduleChime([
        { freq: 523.25, start: 0.0, dur: 0.18 }, // C5
        { freq: 659.25, start: 0.12, dur: 0.32 }, // E5 (major third)
      ]);
    } else if (outcome === 'lose') {
      this.scheduleChime([
        { freq: 392.0, start: 0.0, dur: 0.2 }, // G4
        { freq: 311.13, start: 0.14, dur: 0.36 }, // Eb4 (minor, descending)
      ]);
    } else {
      this.scheduleChime([
        { freq: 440.0, start: 0.0, dur: 0.4 }, // A4 — neutral bell
      ]);
    }
    // Reference nowOffset to keep TS happy if both branches above ever inline.
    void nowOffset;
  }

  // ---------------------------------------------------------------------
  // Engine primitives
  // ---------------------------------------------------------------------

  /** Play a single ToneSpec (one or two voices) with an ADSR-ish gain envelope. */
  private play(spec: ToneSpec): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.master) return;
    // If still autoplay-locked at call time, the tone is simply omitted.
    if (ctx.state === 'suspended') return;

    this.synthesizeVoice(ctx, this.master, spec.freq, spec.type, spec.duration, spec.gain, 0);
    if (spec.second) {
      this.synthesizeVoice(
        ctx,
        this.master,
        spec.second.freq,
        spec.second.type,
        spec.second.duration,
        spec.second.gain,
        0,
        spec.second.detune,
      );
    }
  }

  /** Build one voice (OscillatorNode → GainNode → master) with an envelope. */
  private synthesizeVoice(
    ctx: AudioContext,
    destination: GainNode,
    freq: number,
    type: OscillatorType,
    duration: number,
    peak: number,
    startOffset: number,
    detune?: number,
  ): void {
    const t0 = ctx.currentTime + startOffset;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();

    osc.type = type;
    osc.frequency.value = freq;
    if (detune !== undefined) osc.detune.value = detune;

    // Quick attack → short decay/sustain → exponential release to ~silence.
    const attack = 0.006;
    const release = Math.max(0.02, duration * 0.6);
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(peak, t0 + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration + release);

    osc.connect(env);
    env.connect(destination);
    osc.start(t0);
    osc.stop(t0 + duration + release + 0.02);
    // Self-disconnect on end so the graph never accumulates.
    osc.onended = (): void => {
      osc.disconnect();
      env.disconnect();
    };
  }

  /** Schedule a small sequence of (freq,start,dur) notes for the game-over chime. */
  private scheduleChime(
    notes: ReadonlyArray<{ freq: number; start: number; dur: number }>,
  ): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.master) return;
    if (ctx.state === 'suspended') return;
    for (const n of notes) {
      // Chime voices use a soft sine with a slightly higher peak — bells feel brighter.
      this.synthesizeVoice(ctx, this.master, n.freq, 'sine', n.dur, 0.5, n.start);
    }
  }
}

/**
 * Singleton instance exported for direct, framework-agnostic use (e.g. the socket
 * controller, which lives outside the React tree, or the sound toggle button).
 */
export const audioController = new AudioController();
